/** @file ViewportToolbar: tools, space, pivot, snap, facing, camera, onion, grid, joints, quads. */
import type { ReactElement, ReactNode } from 'react';
import type { Tool } from '../../state/editorTypes';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { useToolAnnounce } from './useToolAnnounce';
import { VIEWPORT_STYLES as S } from './viewportStyles';

const TOOLS: { tool: Tool; key: string; label: string }[] = [
  { tool: 'hand', key: 'Q', label: 'Hand' }, { tool: 'move', key: 'W', label: 'Move' }, { tool: 'rotate', key: 'E', label: 'Rotate' },
  { tool: 'scale', key: 'R', label: 'Scale' }, { tool: 'ik', key: 'T', label: 'IK' },
];
const FACING: { value: string; label: string }[] = [
  { value: 'clip', label: 'clip' }, ...[1, 2, 4, 8, 16, 32].map((n) => ({ value: String(n), label: String(n) })), { value: 'free', label: 'free' },
];

/** A toolbar button, highlighted while `on`. */
function Button({ on = false, title, onClick, children }: { on?: boolean; title: string; onClick: () => void; children: ReactNode }): ReactElement {
  return <button type="button" title={title} aria-pressed={on} style={on ? { ...S.button, ...S.active } : S.button} onClick={onClick}>{children}</button>;
}

/** A labelled number field committing on change (NaN ignored). */
function NumberField({ label, value, step, onValue }: { label: string; value: number; step: number; onValue: (v: number) => void }): ReactElement {
  return (
    <label style={S.label}>{label}{' '}
      <input style={S.input} type="number" step={step} value={Math.round(value * 10) / 10}
        onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) onValue(v); }} />
    </label>
  );
}

/** The tool's name and key while it is announced (switched to, or its key pressed again). */
function Announce({ tool }: { tool: Tool | null }): ReactElement {
  const t = TOOLS.find((x) => x.tool === tool);
  return <span role="status" aria-live="polite" style={{ ...S.label, color: '#f2b544', minWidth: 90 }}>{t === undefined ? '' : `${t.label} tool (${t.key})`}</span>;
}

/**
 * The bar above the viewport; every control reads and writes the store (so on mount it shows the tool, space and
 * pivot the store holds); a tool switch or a second press of the current tool's key is announced for a moment.
 * `scale` is the canvas upscale shown.
 */
export function ViewportToolbar({ scale }: { scale: number }): ReactElement {
  const store = useEditorStore();
  const announced = useToolAnnounce(store);
  const tool = useEditor((s) => s.tool);
  const space = useEditor((s) => s.space);
  const pivot = useEditor((s) => s.pivot);
  const snapDeg = useEditor((s) => s.snapDeg);
  const camera = useEditor((s) => s.camera);
  const a = store.getState();
  const facing = camera.facingSteps === null ? 'clip' : String(camera.facingSteps);
  const onFacing = (v: string): void => { a.setCamera({ facingSteps: v === 'clip' ? null : v === 'free' ? 'free' : Number(v) }); };
  return (
    <div style={S.bar} role="toolbar" aria-label="viewport tools">
      {TOOLS.map((t) => (
        <Button key={t.tool} on={tool === t.tool} title={`${t.label} (${t.key})`} onClick={() => { a.setTool(t.tool); }}>{t.key} {t.label}</Button>
      ))}
      <Announce tool={announced?.tool ?? null} />
      <Button title="Local / World (X)" onClick={() => { a.setSpace(space === 'local' ? 'world' : 'local'); }}>{space === 'local' ? 'Local' : 'World'}</Button>
      <Button title="Pivot / Center (Z)" onClick={() => { a.setPivot(pivot === 'pivot' ? 'center' : 'pivot'); }}>{pivot === 'pivot' ? 'Pivot' : 'Center'}</Button>
      <NumberField label="Snap" value={snapDeg} step={5} onValue={(v) => { a.setSnap(Math.max(0, v)); }} />
      <label style={S.label}>Facing{' '}
        <select style={S.input} value={facing} onChange={(e) => { onFacing(e.target.value); }}>
          {FACING.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </label>
      <NumberField label="Yaw" value={camera.yawDeg} step={15} onValue={(v) => { a.setCamera({ yawDeg: ((v % 360) + 360) % 360 }); }} />
      <NumberField label="Pitch" value={camera.pitchDeg} step={5} onValue={(v) => { a.setCamera({ pitchDeg: v }); }} />
      <span style={S.label}>Onion</span>
      <Button title="Fewer onion frames" onClick={() => { a.setCamera({ onion: Math.max(0, camera.onion - 1) }); }}>-</Button>
      <span>{camera.onion}</span>
      <Button title="More onion frames" onClick={() => { a.setCamera({ onion: Math.min(3, camera.onion + 1) }); }}>+</Button>
      <Button on={camera.grid} title="Ground grid (G)" onClick={() => { a.setCamera({ grid: !camera.grid }); }}>Grid</Button>
      <Button on={camera.joints} title="Joints, outline and gizmo (J)" onClick={() => { a.setCamera({ joints: !camera.joints }); }}>Joints</Button>
      <Button on={camera.quads} title="Part quads: size and pivot handles (Shift+J)" onClick={() => { a.setCamera({ quads: !camera.quads }); }}>Quads</Button>
      <span style={S.label}>x{scale}</span>
    </div>
  );
}
